import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { EMPTY, Observable, catchError, tap } from 'rxjs';
import { MAX_RECENT_SEARCH_TERMS, MIN_SEARCH_TERM_LENGTH } from '@shared/constants/global-search.constants';
import { GlobalSearchResults, SearchResult } from '@shared/models/search-result.model';
import { GlobalSearchService } from '@shared/services/global-search.service';
import { NotificationService } from '@shared/services/notification.service';
import { readRecentTerms, writeRecentTerms } from '@shared/utils/global-search.utils';
import { ClearGlobalSearch, ClearRecentSearchTerms, RememberSearchTerm, SearchGlobally } from './global-search.actions';
import { GlobalSearchStateModel } from './global-search.model';

@State<GlobalSearchStateModel>({
    name: 'globalSearch',
    defaults: {
        term: '',
        loading: false,
        error: false,
        sequences: [],
        nuggets: [],
        recentTerms: readRecentTerms()
    }
})
@Injectable()
export class GlobalSearchState {
    private readonly searchService = inject(GlobalSearchService);
    private readonly notificationService = inject(NotificationService);

    @Selector()
    static term(state: GlobalSearchStateModel): string {
        return state.term;
    }

    @Selector()
    static sequences(state: GlobalSearchStateModel): SearchResult[] {
        return state.sequences;
    }

    @Selector()
    static nuggets(state: GlobalSearchStateModel): SearchResult[] {
        return state.nuggets;
    }

    @Selector()
    static isLoading(state: GlobalSearchStateModel): boolean {
        return state.loading;
    }

    @Selector()
    static hasError(state: GlobalSearchStateModel): boolean {
        return state.error;
    }

    @Selector()
    static resultCount(state: GlobalSearchStateModel): number {
        return state.sequences.length + state.nuggets.length;
    }

    @Selector()
    static recentTerms(state: GlobalSearchStateModel): string[] {
        return state.recentTerms;
    }

    @Action(SearchGlobally, { cancelUncompleted: true })
    public search(
        ctx: StateContext<GlobalSearchStateModel>,
        { payload }: SearchGlobally
    ): Observable<GlobalSearchResults> {
        const term = payload.term.trim();

        if (term.length < MIN_SEARCH_TERM_LENGTH) {
            ctx.patchState({ term, loading: false, error: false, sequences: [], nuggets: [] });
            return EMPTY;
        }

        ctx.patchState({ term, loading: true, error: false });

        return this.searchService.search(term).pipe(
            tap(({ sequences, nuggets }) => ctx.patchState({ sequences, nuggets, loading: false })),
            catchError(() => {
                ctx.patchState({ sequences: [], nuggets: [], loading: false, error: true });
                this.notificationService.showError('Suche konnte nicht ausgeführt werden');
                return EMPTY;
            })
        );
    }

    @Action(ClearGlobalSearch)
    public clear(ctx: StateContext<GlobalSearchStateModel>): void {
        ctx.patchState({ term: '', loading: false, error: false, sequences: [], nuggets: [] });
    }

    @Action(RememberSearchTerm)
    public rememberTerm(ctx: StateContext<GlobalSearchStateModel>, { payload }: RememberSearchTerm): void {
        const term = payload.term.trim();
        if (term.length < MIN_SEARCH_TERM_LENGTH) {
            return;
        }

        const recentTerms = [term, ...ctx.getState().recentTerms.filter(entry => entry !== term)].slice(
            0,
            MAX_RECENT_SEARCH_TERMS
        );

        writeRecentTerms(recentTerms);
        ctx.patchState({ recentTerms });
    }

    @Action(ClearRecentSearchTerms)
    public clearRecentTerms(ctx: StateContext<GlobalSearchStateModel>): void {
        writeRecentTerms([]);
        ctx.patchState({ recentTerms: [] });
    }
}
